<template>
  <div>
    <v-dialog v-model="dialogOpen" max-width="760" scrollable persistent>
      <v-card class="guestbook-dialog" :dark="isDarkMode">
        <div class="guestbook-hero">
          <div class="d-flex align-start justify-space-between">
            <div class="d-flex align-center" style="min-width: 0">
              <div class="guestbook-hero__icon mr-4">
                <v-icon color="white" size="30">mdi-book-open-page-variant-outline</v-icon>
              </div>
              <div style="min-width: 0">
                <div class="guestbook-eyebrow">{{ sourceLabel }}</div>
                <h2 class="guestbook-title text-truncate">Neuer Gästebucheintrag</h2>
                <div class="guestbook-time">
                  <v-icon x-small color="white" class="mr-1">mdi-clock-outline</v-icon>
                  {{ submittedLabel }}
                </div>
              </div>
            </div>
            <v-btn icon dark aria-label="Gästebucheintrag schliessen" @click.stop="close">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <v-card-text class="guestbook-content pa-5 pa-md-7">
          <section class="guestbook-person mb-6">
            <v-avatar size="62" class="guestbook-avatar mr-4">
              <span>{{ initials }}</span>
            </v-avatar>
            <div style="min-width: 0">
              <div class="text-h5 font-weight-black guestbook-text text-truncate">{{ fullName }}</div>
              <div class="d-flex flex-wrap mt-2" style="gap: 8px">
                <v-chip v-if="safeEntry.email" small outlined color="teal" :href="`mailto:${safeEntry.email}`">
                  <v-icon left small>mdi-email-outline</v-icon>{{ safeEntry.email }}
                </v-chip>
                <v-chip v-if="safeEntry.phone" small outlined color="teal" :href="`tel:${safeEntry.phone}`">
                  <v-icon left small>mdi-phone-outline</v-icon>{{ safeEntry.phone }}
                </v-chip>
              </div>
            </div>
          </section>

          <div class="guestbook-detail-grid mb-6">
            <div class="guestbook-detail-card">
              <div class="guestbook-detail-card__label">
                <v-icon small class="mr-1">mdi-bullhorn-outline</v-icon>Aufmerksam geworden durch
              </div>
              <div class="guestbook-detail-card__value">{{ referralLabel || "Keine Angabe" }}</div>
            </div>
            <div class="guestbook-detail-card">
              <div class="guestbook-detail-card__label">
                <v-icon small class="mr-1">mdi-target</v-icon>Trainingsziele
              </div>
              <div v-if="goalLabels.length" class="d-flex flex-wrap" style="gap: 6px">
                <v-chip v-for="goal in goalLabels" :key="goal" x-small color="teal lighten-5" class="teal--text text--darken-3 font-weight-bold">
                  {{ goal }}
                </v-chip>
              </div>
              <div v-else class="guestbook-detail-card__value">Keine Angabe</div>
            </div>
          </div>

          <v-divider class="mb-6" />

          <section>
            <div class="d-flex align-center mb-4">
              <div class="guestbook-section-icon mr-3">
                <v-icon color="teal darken-1">mdi-account-convert-outline</v-icon>
              </div>
              <div>
                <div class="text-h6 font-weight-black guestbook-text">Kundenaccount zuordnen</div>
                <div class="caption grey--text">{{ appointmentDestinationText }}</div>
              </div>
            </div>

            <v-alert v-if="!canManageAccount" text dense type="info" class="mb-0">
              Zum Erstellen oder Verknüpfen eines Kundenaccounts fehlt dir die entsprechende Kundenberechtigung.
            </v-alert>

            <div v-else-if="linkedCustomer" class="guestbook-linked-panel pa-4">
              <div class="d-flex align-center mb-4">
                <v-avatar size="42" color="teal" class="white--text font-weight-bold mr-3">{{ linkedCustomerInitials }}</v-avatar>
                <div style="min-width: 0">
                  <div class="font-weight-black guestbook-text text-truncate">{{ linkedCustomerName }}</div>
                  <div class="caption grey--text text-truncate">{{ linkedCustomer.email || "Account ist bereits mit diesem Lead verbunden" }}</div>
                </div>
              </div>
              <v-btn block large depressed color="teal darken-1" class="white--text font-weight-bold" :loading="linking" @click="confirmExistingLink">
                <v-icon left>mdi-calendar-account-outline</v-icon>Als Kundentermin übernehmen
              </v-btn>
            </div>

            <template v-else>
              <div v-if="!showSearch">
                <v-btn
                  v-if="canCreateAccount"
                  block
                  x-large
                  depressed
                  color="teal darken-1"
                  class="white--text font-weight-bold mb-3"
                  @click="openRegistration">
                  <v-icon left>mdi-account-plus-outline</v-icon>Account erstellen
                </v-btn>
                <v-btn v-if="canLinkAccount" block large outlined color="teal darken-1" class="font-weight-bold" @click="showSearch = true">
                  <v-icon left>mdi-link-variant</v-icon>Mit bestehendem Account verknüpfen
                </v-btn>
              </div>

              <div v-else class="guestbook-search-panel pa-4">
                <div class="font-weight-bold guestbook-text mb-1">Bestehenden Kunden suchen</div>
                <div class="caption grey--text mb-3">Suche nach Name oder E-Mail-Adresse und wähle den passenden Account.</div>
                <user-search
                  return-user
                  ignore-rights
                  :lookup-method="searchGuestbookCustomers"
                  label="Kunde suchen…"
                  :dark="isDarkMode"
                  :disabled="linking"
                  @update="linkCustomer" />
                <div class="d-flex justify-end">
                  <v-btn text small color="grey" :disabled="linking" @click="showSearch = false">Abbrechen</v-btn>
                </div>
              </div>
            </template>
          </section>
        </v-card-text>
      </v-card>
    </v-dialog>

    <register
      :active="showRegisterDialog"
      :auto-login="false"
      :require-password="false"
      :initial-data="registrationPrefill"
      reset-on-open
      @register="newAccountRegistered"
      @close="closeRegistration" />
  </div>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import UserSearch from "@/components/userSearch.vue"
import register from "@/views/launchScreen/register.vue"

const GOAL_LABELS = {
  weight_loss: "Abnehmen",
  muscle_gain: "Muskelaufbau",
  toning: "Straffen",
  endurance: "Ausdauer verbessern",
  back_strength: "Rücken kräftigen",
  pain_relief: "Schmerzen beseitigen",
  movement: "Bewegungsdrang ausleben",
  mobility: "Beweglichkeit verbessern",
  coordination: "Koordination verbessern",
  health: "Gesund bleiben",
  posture: "Haltung verbessern",
  rehabilitation: "Rehabilitation",
  competition: "Wettkampfvorbereitung",
  other: "Anderes Ziel",
}

export default {
  name: "WorkCalendarGuestbookDialog",
  components: { UserSearch, register },
  props: {
    value: { type: Boolean, default: false },
    entry: { type: Object, default: () => ({}) },
    isDarkMode: { type: Boolean, default: false },
    sourceLabel: { type: String, default: "Website · Gästebuch" },
    assignToCurrentUser: { type: Boolean, default: false },
    assignedEmployeeId: { type: String, default: "" },
    assignedEmployeeName: { type: String, default: "" },
    confirmAppointmentCreation: { type: Function, default: () => true },
  },
  data() {
    return {
      userService,
      linking: false,
      showSearch: false,
      showRegisterDialog: false,
      pendingRegistrationEntry: null,
    }
  },
  computed: {
    safeEntry() {
      return this.entry || {}
    },
    dialogOpen: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit("input", value)
      },
    },
    entryVersionKey() {
      const submittedAt = this.safeEntry.walkInCalendar && this.safeEntry.walkInCalendar.submittedAt
      return `${this.safeEntry._id || ""}:${submittedAt || ""}`
    },
    canManageAccount() {
      return this.canCreateAccount || this.canLinkAccount
    },
    canCreateAccount() {
      return userService.hasRight("ab_create", "c_create_customer", "c_create")
    },
    canLinkAccount() {
      return userService.hasRight("ab_create", "u_search")
    },
    appointmentDestinationText() {
      if (this.assignedEmployeeId) {
        const target = this.assignedEmployeeName || "dem ausgewählten Trainer"
        return `Nach der Zuordnung wird der Termin direkt ${target} zugewiesen und die Anamnese geöffnet.`
      }
      return this.assignToCurrentUser
        ? "Nach der Zuordnung wird der Termin direkt dir zugewiesen und die Anamnese geöffnet."
        : "Nach der Zuordnung entsteht ein normaler Kundentermin unter MA Info und die Anamnese wird geöffnet."
    },
    fullName() {
      return `${this.safeEntry.name || ""} ${this.safeEntry.surname || ""}`.trim() || "Unbekannter Gast"
    },
    initials() {
      return `${String(this.safeEntry.name || "").charAt(0)}${String(this.safeEntry.surname || "").charAt(0)}`.toUpperCase() || "G"
    },
    submittedLabel() {
      const submittedAt = this.safeEntry.walkInCalendar && this.safeEntry.walkInCalendar.submittedAt
      if (submittedAt) return this.$moment(submittedAt).format("DD.MM.YYYY, HH:mm")
      const date = this.safeEntry.walkInCalendar && this.safeEntry.walkInCalendar.date
      const time = this.safeEntry.walkInCalendar && this.safeEntry.walkInCalendar.time
      return [date ? this.$moment(date).format("DD.MM.YYYY") : "", time].filter(Boolean).join(", ")
    },
    referralLabel() {
      if (!this.safeEntry.referralSource) return ""
      if (this.safeEntry.referralSource === "Andere" && this.safeEntry.referralSourceOther) return `Andere: ${this.safeEntry.referralSourceOther}`
      return this.safeEntry.referralSource
    },
    goalLabels() {
      return (this.safeEntry.goals || []).map((goal) => {
        if (goal === "other" && this.safeEntry.goalOther) return `Anderes Ziel: ${this.safeEntry.goalOther}`
        return GOAL_LABELS[goal] || goal
      })
    },
    linkedCustomer() {
      return this.safeEntry.roodyUser && typeof this.safeEntry.roodyUser === "object" ? this.safeEntry.roodyUser : null
    },
    linkedCustomerName() {
      if (!this.linkedCustomer) return "Verknüpfter Account"
      return `${this.linkedCustomer.vorname || ""} ${this.linkedCustomer.nachname || ""}`.trim() || "Verknüpfter Account"
    },
    linkedCustomerInitials() {
      if (!this.linkedCustomer) return "K"
      return `${String(this.linkedCustomer.vorname || "").charAt(0)}${String(this.linkedCustomer.nachname || "").charAt(0)}`.toUpperCase() || "K"
    },
    registrationPrefill() {
      return {
        email: this.safeEntry.email || "",
        vorname: this.safeEntry.name || "",
        nachname: this.safeEntry.surname || "",
        telefon: this.safeEntry.phone || "",
      }
    },
  },
  watch: {
    value(value) {
      if (!value) this.resetLocalState()
    },
    entryVersionKey(value, previousValue) {
      if (value !== previousValue) this.resetLocalState()
    },
  },
  methods: {
    searchGuestbookCustomers(search, signal) {
      return api.shiftManagement.searchGuestbookCustomers(search, signal)
    },
    resetLocalState() {
      this.linking = false
      this.showSearch = false
      this.showRegisterDialog = false
      this.pendingRegistrationEntry = null
    },
    close() {
      this.dialogOpen = false
    },
    openRegistration() {
      const walkInCalendar = this.safeEntry.walkInCalendar || {}
      this.pendingRegistrationEntry = {
        _id: this.safeEntry._id,
        name: this.safeEntry.name,
        surname: this.safeEntry.surname,
        walkInCalendar: {
          submittedAt: walkInCalendar.submittedAt,
          date: walkInCalendar.date,
          time: walkInCalendar.time,
          appointment: walkInCalendar.appointment,
        },
      }
      this.showRegisterDialog = true
    },
    closeRegistration() {
      this.showRegisterDialog = false
      this.pendingRegistrationEntry = null
    },
    async confirmExistingLink() {
      if (!this.linkedCustomer || !this.linkedCustomer._id) return
      await this.linkCustomer(this.linkedCustomer)
    },
    async linkCustomer(customer, sourceEntry = this.safeEntry) {
      if (this.linking) return
      const submittedAt = sourceEntry && sourceEntry.walkInCalendar && sourceEntry.walkInCalendar.submittedAt
      if (!customer || !customer._id || !sourceEntry || !sourceEntry._id || !submittedAt) {
        this.$toast?.error("Der Gästebucheintrag konnte nicht eindeutig zugeordnet werden. Bitte öffne ihn erneut.")
        return
      }

      const walkInCalendar = sourceEntry.walkInCalendar
      if (!walkInCalendar.appointment && !this.confirmAppointmentCreation([{ date: walkInCalendar.date, start: walkInCalendar.time }])) return

      this.linking = true
      try {
        const result = await api.shiftManagement.linkGuestbookCustomer(sourceEntry._id, customer._id, submittedAt, {
          assignToCurrentUser: this.assignToCurrentUser,
          assignedEmployeeId: this.assignedEmployeeId,
        })
        const linkedCustomer = result?.customer || customer
        const name = `${linkedCustomer.vorname || sourceEntry.name || ""} ${linkedCustomer.nachname || sourceEntry.surname || ""}`.trim()
        const employeeName = result?.assignedEmployeeName || this.assignedEmployeeName
        const destination = result?.assignedToEmployee
          ? employeeName
            ? `${employeeName} als Termin zugewiesen`
            : "dem ausgewählten Trainer als Termin zugewiesen"
          : result?.assignedToCurrentUser
            ? "dir als Termin zugewiesen"
            : "als Termin unter MA Info angelegt"
        this.$toast?.success(`${name || "Der Kunde"} wurde verknüpft und ${destination}.`)
        if (result?.assignmentWarning) this.$toast?.info(result.assignmentWarning)
        this.$emit("resolved", { leadId: sourceEntry._id, customer: linkedCustomer, appointment: result?.appointment || null })
        this.dialogOpen = false
      } catch (error) {
        const message = error?.response?.data?.message || "Der Account konnte nicht verknüpft werden."
        this.$toast?.error(message)
        if (error?.response?.status === 409) {
          this.$emit("refresh")
          this.$emit("input", false)
        }
      } finally {
        this.linking = false
      }
    },
    async newAccountRegistered(registrationResult) {
      this.showRegisterDialog = false
      const sourceEntry = this.pendingRegistrationEntry
      this.pendingRegistrationEntry = null
      const customer = registrationResult?.user || registrationResult
      if (!customer || !customer._id) {
        this.$toast?.error("Der Account wurde erstellt, konnte aber nicht automatisch verknüpft werden.")
        return
      }
      await this.linkCustomer(customer, sourceEntry)
    },
  },
}
</script>

<style lang="scss" scoped>
.guestbook-dialog {
  border-radius: 22px !important;
  overflow: hidden;
}

.guestbook-hero {
  padding: 24px 28px;
  color: #fff;
  background:
    radial-gradient(circle at 92% 15%, rgba(255, 255, 255, 0.24), transparent 30%),
    linear-gradient(135deg, #00695c 0%, #00897b 55%, #26a69a 100%);
}

.guestbook-hero__icon {
  width: 56px;
  height: 56px;
  border-radius: 17px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.28);
  box-shadow: 0 14px 30px rgba(0, 77, 64, 0.25);
}

.guestbook-eyebrow {
  font-size: 0.69rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.76;
}

.guestbook-title {
  margin: 2px 0;
  font-size: clamp(1.3rem, 3vw, 1.75rem);
  line-height: 1.15;
}

.guestbook-time {
  display: flex;
  align-items: center;
  font-size: 0.78rem;
  font-weight: 700;
  opacity: 0.86;
}

.guestbook-content {
  background: var(--v-background-base, #f7faf9);
}

.guestbook-person {
  display: flex;
  align-items: center;
}

.guestbook-avatar {
  flex: 0 0 auto;
  color: #00695c;
  font-size: 1.15rem;
  font-weight: 900;
  background: linear-gradient(135deg, #e0f2f1, #b2dfdb);
  border: 3px solid rgba(0, 137, 123, 0.14);
}

.guestbook-text {
  color: var(--v-text-base, rgba(0, 0, 0, 0.87));
}

.guestbook-detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.guestbook-detail-card,
.guestbook-search-panel,
.guestbook-linked-panel {
  border-radius: 16px;
  border: 1px solid rgba(0, 137, 123, 0.15);
  background: rgba(0, 137, 123, 0.055);
}

.guestbook-detail-card {
  padding: 16px;
}

.guestbook-detail-card__label {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  color: #00897b;
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.guestbook-detail-card__value {
  font-weight: 800;
  line-height: 1.35;
}

.guestbook-section-icon {
  width: 44px;
  height: 44px;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 13px;
  background: rgba(0, 137, 123, 0.11);
}

.theme--dark {
  .guestbook-content {
    background: #171b1a;
  }

  .guestbook-text,
  .guestbook-detail-card__value {
    color: rgba(255, 255, 255, 0.94);
  }

  .guestbook-detail-card,
  .guestbook-search-panel,
  .guestbook-linked-panel {
    border-color: rgba(128, 203, 196, 0.2);
    background: rgba(38, 166, 154, 0.1);
  }
}

@media (max-width: 600px) {
  .guestbook-hero {
    padding: 20px;
  }

  .guestbook-hero__icon {
    width: 48px;
    height: 48px;
    border-radius: 14px;
  }

  .guestbook-detail-grid {
    grid-template-columns: 1fr;
  }

  .guestbook-person {
    align-items: flex-start;
  }
}
</style>
